import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { content } from '../data/content';
import { theme, combineClasses } from '../theme/theme';

/**
 * ============================================================================
 * 文件模块：components/NewsCarousel.jsx
 * [说明]: 滚动新闻轮播组件，在主页展示最新的3条新闻
 * ============================================================================
 */
const NewsCarousel = ({ lang, themeMode = "dark", onNewsClick }) => {
  const isLight = themeMode === "light";
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  // 获取精选新闻（最多3条）
  const featuredNews = content.news.items.filter(item => item.featured).slice(0, 3);
  
  // 自动轮播
  useEffect(() => {
    if (!isAutoPlaying || featuredNews.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featuredNews.length);
    }, 5000); // 每5秒切换一次
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, featuredNews.length]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredNews.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredNews.length) % featuredNews.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === 'cn' ? 'zh-CN' : 'en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (featuredNews.length === 0) return null;

  const currentNews = featuredNews[currentIndex];

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* 主要新闻卡片 - 单行紧凑设计 */}
      <div 
        className={combineClasses(
          "relative overflow-hidden rounded-lg backdrop-blur-md cursor-pointer",
          isLight 
            ? "bg-gradient-to-r from-gray-100 to-gray-50 border border-gray-200 hover:border-cyan-500/50"
            : "bg-gradient-to-r from-white/10 to-white/5 border border-white/20 hover:border-cyan-400/50",
          "transition-all duration-300 h-16 flex items-center px-4"
        )}
        onClick={() => onNewsClick && onNewsClick()}
      >
        {/* 背景光晕 */}
        <div className={combineClasses(
          "absolute top-0 right-0 w-24 h-24 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300",
          isLight 
            ? "bg-gradient-to-br from-cyan-400/5 to-purple-500/5" 
            : "bg-gradient-to-br from-cyan-400/10 to-purple-500/10"
        )} />
        
        {/* 内容 - 单行布局 */}
        <div className="relative z-10 flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* 日期 */}
            <div className="flex items-center gap-1 text-cyan-400 text-xs font-mono flex-shrink-0">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(currentNews.date)}</span>
            </div>
            
            {/* 标题 - 单行显示 */}
            <h3 className={combineClasses(
              "text-sm font-semibold truncate transition-colors flex-1",
              isLight 
                ? "text-gray-900 group-hover:text-cyan-600" 
                : "text-white group-hover:text-cyan-300"
            )}>
              {currentNews.title[lang]}
            </h3>
          </div>

          {/* 导航控制和进度指示器 - 合并在一起 */}
          {featuredNews.length > 1 && (
            <div className="flex-shrink-0 flex items-center gap-2">
              {/* 进度点 */}
              <div className="flex gap-1">
                {featuredNews.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      goToSlide(index);
                    }}
                    className={combineClasses(
                      "w-1.5 h-1.5 rounded-full transition-all duration-300",
                      currentIndex === index
                        ? "bg-cyan-400 w-4"
                        : isLight 
                          ? "bg-gray-400/50 hover:bg-gray-600/50" 
                          : "bg-white/30 hover:bg-white/50"
                    )}
                  />
                ))}
              </div>
              
              {/* 导航按钮 */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrev();
                }}
                className={combineClasses(
                  "p-1 rounded-full transition-all opacity-0 group-hover:opacity-100",
                  isLight 
                    ? "bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-900" 
                    : "bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white"
                )}
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className={combineClasses(
                  "p-1 rounded-full transition-all opacity-0 group-hover:opacity-100",
                  isLight 
                    ? "bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-900" 
                    : "bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white"
                )}
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>

        {/* 自动播放进度条 */}
        {isAutoPlaying && featuredNews.length > 1 && (
          <div className="absolute bottom-0 left-0 h-0.5 bg-cyan-400/30 w-full">
            <div 
              className="h-full bg-gradient-to-r from-cyan-400 to-purple-500 transition-all duration-[5000ms] ease-linear"
              style={{ 
                width: isAutoPlaying ? '100%' : '0%',
                animation: isAutoPlaying ? 'progress 5s linear infinite' : 'none'
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default NewsCarousel;
